'use client';
import React, { useEffect, useRef, useState } from 'react';

const STEPS = [
  {
    n: '01',
    action: 'Describe your incident',
    detail: 'Write what happened — "Missed my train, found $50 on the platform" — in plain words.',
    field: 'Missed my train, found $50 on the platform',
    color: '#3DFFC4',
  },
  {
    n: '02',
    action: 'Tag the luck type',
    detail: 'Lucky Escape · Windfall · Serendipity · Near Miss — pick the one that fits.',
    field: 'Lucky Escape 🍀',
    color: '#B4A0FF',
  },
  {
    n: '03',
    action: 'Hit post — go live',
    detail: 'Your incident joins the global feed instantly. No account needed. Anonymous by default.',
    field: 'Posted to live feed ✓',
    color: '#3DFFC4',
  },
];

export default function ThreeClickSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((s) => (s + 1) % STEPS?.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.15 }
    );
    sectionRef?.current?.querySelectorAll('.reveal-up')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 relative">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(180,160,255,0.06) 0%, transparent 60%)' }}
      />
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16 reveal-up">
          <span className="badge-lilac text-xs font-display font-700 px-3 py-1.5 rounded-full uppercase tracking-wider">
            30-second proof
          </span>
          <h2 className="mt-4 text-[clamp(2rem,4vw,3rem)] font-display font-700 text-iwhite leading-tight tracking-tight">
            Log your luck in three steps.<br />
            <span style={{ color: '#3DFFC4' }}>No account. No friction.</span>
          </h2>
          <p className="mt-4 text-lg text-white/50 font-body max-w-lg mx-auto">
            Watch the flow. This is exactly how a unique visitor posts a luck incident — start to live feed.
          </p>
        </div>

        {/* Frosted card */}
        <div
          className="reveal-up delay-200 rounded-3xl p-8 lg:p-12 relative overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(61,255,196,0.08)',
          }}
        >
          {/* Top bar */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-3 h-3 rounded-full bg-white/10" />
            <div className="w-3 h-3 rounded-full bg-white/10" />
            <div className="w-3 h-3 rounded-full bg-mint/40" />
            <div className="ml-3 flex-1 h-7 rounded-lg bg-white/[0.04] flex items-center px-3">
              <span className="text-xs text-white/30 font-mono">luckcount.so/post</span>
            </div>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
            {STEPS?.map((step, i) => (
              <div
                key={step?.n}
                className="relative rounded-2xl p-5 transition-all duration-500"
                style={{
                  background: activeStep === i
                    ? `rgba(${step?.color === '#3DFFC4' ? '61,255,196' : '180,160,255'},0.08)`
                    : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${activeStep === i ? step?.color + '40' : 'rgba(255,255,255,0.06)'}`,
                  boxShadow: activeStep === i ? `0 0 20px ${step?.color}15` : 'none',
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="text-xs font-mono font-700"
                    style={{ color: activeStep === i ? step?.color : 'rgba(255,255,255,0.2)' }}
                  >
                    {step?.n}
                  </span>
                  {activeStep === i && (
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: step?.color, boxShadow: `0 0 6px ${step?.color}` }}
                    />
                  )}
                </div>
                <h4 className="text-base font-display font-700 text-iwhite mb-1.5">{step?.action}</h4>
                <p className="text-xs text-white/40 font-body leading-relaxed mb-4">{step?.detail}</p>

                <div
                  className="rounded-xl px-3 py-2.5 font-mono text-sm"
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    border: `1px solid ${activeStep === i ? step?.color + '50' : 'rgba(255,255,255,0.06)'}`,
                    color: activeStep === i ? step?.color : 'rgba(255,255,255,0.3)',
                  }}
                >
                  {step?.field}
                  {activeStep === i && (
                    <span className="inline-block w-0.5 h-4 ml-0.5 align-middle bg-current animate-pulse" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Result card */}
          <div
            className="rounded-2xl p-6 flex items-center justify-between"
            style={{
              background: 'rgba(61,255,196,0.06)',
              border: '1px solid rgba(61,255,196,0.25)',
            }}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(61,255,196,0.15)', border: '1px solid rgba(61,255,196,0.3)' }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3DFFC4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-display font-700 text-mint">Incident live. Visible to the world.</p>
                <p className="text-xs text-white/40 font-body mt-0.5">Tagged · Timestamped · Added to global feed — in under 30 seconds</p>
              </div>
            </div>
            <div className="hidden lg:block text-right">
              <div className="text-2xl font-display font-700 text-mint">30s</div>
              <div className="text-xs text-white/30 font-body">avg. post time</div>
            </div>
          </div>

          <div
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(61,255,196,0.08) 0%, transparent 70%)' }}
          />
        </div>
      </div>
    </section>
  );
}